import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import _get from 'lodash/get';
import { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  Attachment,
  Documentation,
  DocumentationFilterInputObject,
  DocumentationValidationStatusEnum,
} from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
  useEdmDocumentationsLightQuery,
} from '@lib/graphql-common';
import Accordion from '@lib/components/Accordion/Accordion';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import LinkComponent, { LinkSizes, LinkTypes } from '@lib/components/Link/Link';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Typography from '@lib/components/Typography/Typography';
import toast from '@lib/components/Toast/Toast';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { PER_PAGE_KEY_DEFAULT } from '@lib/enums/urls';
import getDocumentActionsMenu from './utils/getDocumentActionsMenu';
import { LinkedDocumentsVariant } from './enums';
import styles from './LinkedDocuments.module.scss';

export interface LinkedDocumentsProps {
  filters: DocumentationFilterInputObject[];
  icon?: string;
  label?: string;
  variant?: LinkedDocumentsVariant;
  getDetailsUrl?: (doc: Documentation) => string | undefined;
  useFavoritesTool?: boolean;
  useMarkAsReadTool?: boolean;
  showUnavailableState?: boolean;
  onMarkedAsRead?: (
    documentationId: string,
    versionId: string,
    versionViewId: string,
  ) => void;
}

/**
 * Paginated card list of `Documentation` entities that match the supplied
 * filters. Shared between admin and user apps (e.g. "Task-related documents",
 * "Form-related documents", asset/area operational-scope tabs).
 *
 * Responsibilities:
 * - Fires `edmDocumentations` with the given `filters` and a `cache-and-network`
 *   policy; paginates by incrementing `page` and appending unique ids into a
 *   local `allDocuments` buffer. Resets when `filters` change.
 * - Renders each item as a `DocumentationPreview` (row card) with an overflow
 *   menu built by `getDocumentActionsMenu` — View document, Version notes,
 *   Details (navigates via `getDetailsUrl`), and optionally Favorite.
 * - Owns the secondary modals it triggers: `FileViewerModal`,
 *   `DocumentVersionNotes`.
 * - Handles favorite toggling via `documentationBulkFavorite` /
 *   `documentationBulkUnfavorite` with an optimistic `cache.modify` update.
 *   Mark-as-read (when `useMarkAsReadTool` is enabled) is handled entirely
 *   inside `FileViewerModal` — it settles into the same
 *   `DocumentationVersionView` record passive view tracking already accrued
 *   for the open session, then explicitly confirms the read, and only then
 *   calls back into `onMarkAsRead` here,
 *   which also flips the cached `Documentation.read` flag so `FileViewerModal`
 *   hides its CTA if the same doc is reopened later this session.
 *
 * Layout variants:
 * - `Default` — bare content. Renders nothing until the first page arrives
 *   unless `label`+`icon` are provided, in which case it wraps the content in
 *   an `EntityView` label/value block.
 * - `WithAccordion` — same content inside a collapsible `Accordion` (default
 *   collapsed). Use for the top-of-form "Task-related / Form-related" sections.
 *
 * Empty behavior: returns `null` when the query settles with zero results, so
 * the component is safe to drop in conditionally without producing stray
 * headers/placeholders.
 *
 * Opt-in tools:
 * - `useFavoritesTool` — shows favorite icon prefix on cards, adds the
 *   Add/Remove-favorite menu entry, and wires the favorite button inside
 *   `FileViewerModal`.
 * - `useMarkAsReadTool` — enables the "Mark as read" affordance in
 *   `FileViewerModal`.
 * - `showUnavailableState` — when set, any document whose `validationStatus`
 *   is not `Validated` is rendered as a non-interactive "Document unavailable"
 *   message instead of a preview card. Used in the user app to keep
 *   require-user document lists in parity with `DocumentQuestionOption`; the
 *   admin app intentionally keeps showing such documents so admins can manage
 *   their validation.
 */
export default function LinkedDocuments({
  filters,
  icon,
  label,
  variant = LinkedDocumentsVariant.Default,
  getDetailsUrl,
  useFavoritesTool = false,
  useMarkAsReadTool = false,
  showUnavailableState = false,
  onMarkedAsRead,
}: LinkedDocumentsProps) {
  const withSection = !!(label && icon);
  const isAccordion = variant === LinkedDocumentsVariant.WithAccordion;
  const isLinkList = variant === LinkedDocumentsVariant.LinkList;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cache } = useApolloClient();
  const [page, setPage] = useState(1);

  const { data, previousData, loading, refetch } =
    useEdmDocumentationsLightQuery({
      fetchPolicy: isLinkList ? 'cache-first' : 'cache-and-network',
      variables: {
        page,
        limit: +PER_PAGE_KEY_DEFAULT,
        filters,
      },
      skip: !filters?.length,
    });

  const queryData = data || previousData;
  const newDocuments = useMemo(
    () => (_get(queryData, 'data.collection', []) || []) as Documentation[],
    [queryData],
  );
  // Seeded from `newDocuments` (rather than `[]`) so a fresh mount whose
  // query result is already cached (e.g. a cell that gets recreated after a
  // sibling refetch) renders immediately instead of waiting for a data
  // change that will never come.
  const [allDocuments, setAllDocuments] =
    useState<Documentation[]>(newDocuments);
  const totalPages = _get(queryData, 'data.metadata.totalPages', 1) as number;
  const firstLoading = loading && !previousData;
  const hasMore = page < totalPages;

  const filtersKey = JSON.stringify(filters);
  const prevFiltersKeyRef = useRef(filtersKey);
  const prevQueryDataRef = useRef(queryData);

  if (prevFiltersKeyRef.current !== filtersKey) {
    prevFiltersKeyRef.current = filtersKey;
    setPage(1);
    setAllDocuments([]);
  } else if (prevQueryDataRef.current !== queryData) {
    prevQueryDataRef.current = queryData;
    if (newDocuments.length && page > 1) {
      setAllDocuments((prev) => {
        const existingIds = new Set(prev.map((d) => d.id));
        const unique = newDocuments.filter((d) => !existingIds.has(d.id));
        return [...prev, ...unique];
      });
    } else if (page === 1) {
      setAllDocuments(newDocuments);
    }
  }

  const [fileViewerDocument, setFileViewerDocument] =
    useState<Documentation | null>(null);
  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);

  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const isFavoriteLoading = favoriteLoading || unfavoriteLoading;

  const onToggleFavorite = useCallback(
    (item: Documentation) => {
      const isFavorited = item.favorited;
      const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
      const successMessage = isFavorited
        ? t('remove-from-favorites-success')
        : t('add-to-favorites-success');
      mutation({
        variables: { ids: [item.id] },
        update: (mutationCache: ApolloCache) => {
          mutationCache.modify({
            id: mutationCache.identify(item),
            fields: {
              favorited: () => !isFavorited,
            },
          });
        },
        onCompleted: () => {
          toast({ content: successMessage });
        },
      });
    },
    [bulkFavorite, bulkUnfavorite, t],
  );

  const onViewDocument = useCallback((item: Documentation) => {
    setFileViewerDocument(item);
  }, []);

  // `FileViewerModal` only calls this once its own view-tracking has
  // created/incremented the record and shown its own success/error feedback,
  // so this just needs to remember the id and close. Also flips the cached
  // `Documentation.read` flag so re-opening the same doc later in this
  // session (before any refetch) still sees it as already read, instead of
  // the CTA reappearing.
  const onMarkAsRead = useCallback(
    (_readingDurationSeconds: number, versionViewId: string) => {
      const versionId = fileViewerDocument?.currentVersion?.id;
      const documentationId = fileViewerDocument?.id;
      if (!versionId || !documentationId) return;
      cache.modify({
        id: cache.identify(fileViewerDocument as Documentation),
        fields: { read: () => true },
      });
      onMarkedAsRead?.(documentationId, versionId, versionViewId);
      setFileViewerDocument(null);
    },
    [cache, fileViewerDocument, onMarkedAsRead],
  );

  const onVersionNotes = useCallback((item: Documentation) => {
    setVersionNotesDocument(item);
  }, []);

  const onDetails = useCallback(
    (item: Documentation) => {
      const url = getDetailsUrl?.(item);
      if (url) navigate(url);
    },
    [getDetailsUrl, navigate],
  );

  const onLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const fileViewerPermissions =
    getDocumentationFilePermissions(fileViewerDocument);

  const documentCards = useMemo(
    () =>
      allDocuments.map((item) => {
        const detailsUrl = getDetailsUrl?.(item);
        const isUnavailable =
          showUnavailableState &&
          item.validationStatus !== DocumentationValidationStatusEnum.Validated;
        if (isUnavailable) {
          return (
            <SupportiveMessage
              key={item.id}
              text={t('document-unavailable')}
              leftIconSymbolsOutlined
              color={SupportiveMessageColor.Gray}
              hasBottomSpace={false}
            />
          );
        }
        if (isLinkList) {
          return (
            <LinkComponent
              key={item.id}
              to={detailsUrl}
              component={detailsUrl ? NavLink : undefined}
              size={LinkSizes.small}
              data-test-id="linked-documents__item-link"
            >
              {item.name}
            </LinkComponent>
          );
        }
        const actionsMenu = getDocumentActionsMenu({
          item,
          onDetails,
          onVersionNotes,
          onViewDocument,
          onToggleFavorite: useFavoritesTool ? onToggleFavorite : undefined,
          hasDetailsUrl: !!detailsUrl,
          t,
        });
        return (
          <DocumentationPreview
            key={item.id}
            attachment={item.currentVersion?.attachmentAttached}
            view={DocumentationPreviewEnum.DocumentRowCard}
            customName={item.name}
            notes={item.currentVersion?.notes || undefined}
            isFavorite={useFavoritesTool ? !!item.favorited : undefined}
            onClick={detailsUrl ? () => onDetails(item) : undefined}
            actions={
              <IconButtonWithDropDown
                buttonProps={{
                  icon: 'more_vert',
                }}
                popoverProps={{
                  width: 220,
                  placement: 'bottomRight',
                }}
                navItems={actionsMenu}
              />
            }
          />
        );
      }),
    [
      allDocuments,
      getDetailsUrl,
      isLinkList,
      onDetails,
      onToggleFavorite,
      onVersionNotes,
      onViewDocument,
      useFavoritesTool,
      showUnavailableState,
      t,
    ],
  );

  if (firstLoading) {
    return withSection || isAccordion || isLinkList ? null : <CircleLoader />;
  }

  if (!allDocuments.length && !loading) {
    return null;
  }

  if (isLinkList) {
    return (
      <CircleLoaderWrapper isLoading={loading}>
        <div className={styles.linkList}>{documentCards}</div>
        {hasMore && (
          <div className={styles.loadMore}>
            <LinkComponent
              size={LinkSizes.small}
              type={LinkTypes.secondaryGray}
              onClick={onLoadMore}
              data-test-id="linked-documents__show-more"
              disabled={loading}
            >
              {t('show-more')}
            </LinkComponent>
          </div>
        )}
      </CircleLoaderWrapper>
    );
  }

  const content = (
    <CircleLoaderWrapper isLoading={loading}>
      <div className={styles.documentsList}>{documentCards}</div>
      {hasMore && (
        <div className={styles.loadMore}>
          <LinkComponent
            size={LinkSizes.small}
            type={LinkTypes.secondaryGray}
            onClick={onLoadMore}
            data-test-id="linked-documents__show-more"
            disabled={loading}
          >
            {t('show-more')}
          </LinkComponent>
        </div>
      )}
      <FileViewerModal
        attachment={
          _get(fileViewerDocument, [
            'currentVersion',
            'attachmentAttached',
          ]) as Attachment
        }
        isOpen={!!fileViewerDocument}
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        onClose={() => setFileViewerDocument(null)}
        useDownloadTool={fileViewerPermissions.allowDownload}
        usePrintTool={fileViewerPermissions.allowPrint}
        useFavoriteTool={useFavoritesTool}
        onFavoriteToggle={
          useFavoritesTool && fileViewerDocument
            ? () => onToggleFavorite(fileViewerDocument)
            : undefined
        }
        isFavorited={fileViewerDocument?.favorited ?? false}
        isFavoriteLoading={isFavoriteLoading}
        useMarkAsReadTool={useMarkAsReadTool}
        isMarkedAsRead={fileViewerDocument?.read === true}
        onMarkAsRead={useMarkAsReadTool ? onMarkAsRead : undefined}
        onRefetchAttachment={() => refetch()}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={() => setVersionNotesDocument(null)}
      />
    </CircleLoaderWrapper>
  );

  if (isAccordion) {
    return (
      <Accordion
        title={label}
        className={styles.accordion}
        data-test-id="linked-documents__accordion"
        defaultOpen={false}
      >
        {content}
      </Accordion>
    );
  }

  if (!withSection) return content;

  return (
    <div
      className={classNames(entityViewStyles.infoItem, entityViewStyles.block)}
    >
      <p className={entityViewStyles.infoItemLabel}>
        <MaterialIcon icon={icon as string} symbolsOutlined />
        <Typography variant="caption">{label}</Typography>
      </p>
      <div className={entityViewStyles.infoItemContentFullWidth}>{content}</div>
    </div>
  );
}
